import { XIconNew } from '@/components/svg/x-icon-new/x-icon-new'
import { NavSearch } from '../nav-search/nav-search'
import s from './main-nav-mobile.module.css'
import Link from 'next/link'
import { ArrowRight } from '@/components/svg/arrow-right/arrow-right'
import { NavButton } from '../nav-button/nav-button'
import { UserIcon } from '@/components/svg/user-icon/user-icon'
import { ROUTES } from '@/constants'
import cx from 'classnames'
import { useOnClickOutside, useScrollLock } from 'usehooks-ts'
import { Fragment, useEffect, useRef, useState, useCallback } from 'react'
import { useUser } from '@/hooks/use-user'
import { useFeatureFlag } from '@/hooks/use-feature-flag'
import { ArrowLeftLong } from '@/components/svg/arrow-left-long/arrow-left-long'
import { MenuItemsType } from '../../../../v2/organisms/navigation/navigation-index/navigation-index'
import { getMainCategoryObject, splitSubcategories } from '../main-nav/main-nav'
import { navItemOnClickGTMEvent } from '@/utils/gtmEvents/navItemOnClickGTMEvent'
import { NavSubcategoryList } from '@/components/v3/organisms/navigation/nav-subcategory-list/nav-subcategory-list'
import { NavShopMenuMobile } from '@/components/v3/organisms/navigation/nav-shop-mega-menu/nav-shop-menu-mobile'
import { hasShopMegaMenuLinks } from '@/components/v3/organisms/navigation/nav-shop-mega-menu/shop-mega-menu-links'
import { NewBadge } from '@/components/v3/atoms/new-badge/new-badge'
import type { ShopMegaMenuData } from '@/types/api/shop-mega-menu'

type Props = {
  isMenuOpen: boolean
  onOutsideNavClick: () => void
  menuItems: MenuItemsType
  shopMegaMenu?: ShopMegaMenuData | null
}

// Shop expands like a category, so it shares `openCategorySlug` with them under
// a slug no article category can take.
const SHOP_SLUG = 'shop'

export const MainNavMobile = ({
  isMenuOpen,
  onOutsideNavClick,
  menuItems,
  shopMegaMenu,
}: Props) => {
  const { user, isAdmin, isSupplier, isReader, logOut } = useUser()
  const [openCategorySlug, setOpenCategorySlug] = useState<string | null>(null)
  const [mainCategoriesScrollY, setMainCategoriesScrollY] = useState(0)
  const mainCategoriesRef = useRef<HTMLDivElement>(null)
  const subcategoriesRef = useRef<{ [slug: string]: HTMLDivElement }>({})
  const refContainer = useRef<HTMLDivElement>(null)

  useOnClickOutside(
    refContainer as React.RefObject<HTMLElement>,
    handleCloseMenu
  )
  useScrollLock({ autoLock: isMenuOpen })

  function getProfileLink() {
    if (isAdmin) return ROUTES.ADMIN.ROOT
    if (isSupplier) return ROUTES.SUPPLIER.SUMMARY
    if (isReader) return ROUTES.CUSTOMER.DASHBOARD
    return ''
  }
  const profileLink = getProfileLink()

  function handleCloseMenu() {
    setOpenCategorySlug(null)
    setMainCategoriesScrollY(0)
    if (mainCategoriesRef.current) {
      mainCategoriesRef.current.scrollTo({
        top: mainCategoriesScrollY,
        behavior: 'instant',
      })
    }
    onOutsideNavClick()
  }

  useEffect(() => {
    if (!openCategorySlug && mainCategoriesRef.current) {
      mainCategoriesRef.current.scrollTo({
        top: mainCategoriesScrollY,
        behavior: 'instant',
      })
    }
  }, [mainCategoriesScrollY, openCategorySlug])

  useEffect(() => {
    if (openCategorySlug && subcategoriesRef.current[openCategorySlug]) {
      subcategoriesRef.current[openCategorySlug].scrollTo({
        top: 0,
        behavior: 'instant',
      })
    }
  }, [openCategorySlug])

  const registerSubcategoryRef = useCallback(
    (slug: string, el: HTMLDivElement) => {
      if (el) subcategoriesRef.current[slug] = el
    },
    []
  )

  const handleMainCategoryClick = (slug?: string) => {
    setMainCategoriesScrollY(mainCategoriesRef?.current?.scrollTop || 0)
    setOpenCategorySlug(slug || '')
  }

  const hasShopMenu = hasShopMegaMenuLinks(shopMegaMenu)

  return (
    <div className={cx(s.overlay, { [s.open]: isMenuOpen })}>
      <div
        className={cx(s.container, { [s.open]: isMenuOpen })}
        ref={refContainer}
      >
        <div className={s.topWrapper}>
          {openCategorySlug ? (
            <button
              className={s.back}
              onClick={() => setOpenCategorySlug(null)}
            >
              <ArrowLeftLong svgProps={{ width: 16, height: 16 }} /> Back
            </button>
          ) : (
            <NavSearch onSubmit={handleCloseMenu} />
          )}

          <button
            className={s.close}
            onClick={onOutsideNavClick}
            aria-label="Close menu"
          >
            <XIconNew iconColor="#fff" svgProps={{ width: 24, height: 24 }} />
          </button>
        </div>

        <nav className={s.nav} ref={mainCategoriesRef}>
          {!openCategorySlug && (
            <MainNavMobileList
              menuItems={menuItems}
              hasShopMenu={hasShopMenu}
              onCategoryClick={handleMainCategoryClick}
              onLinkClick={handleCloseMenu}
            />
          )}

          {hasShopMenu && shopMegaMenu && openCategorySlug === SHOP_SLUG && (
            <NavShopMenuMobile
              data={shopMegaMenu}
              isOpen
              onNavigate={handleCloseMenu}
              registerRef={registerSubcategoryRef}
            />
          )}

          {menuItems
            .filter((item) => item.subcategories.length)
            .map((item) => {
              const mainCatObj = getMainCategoryObject(item.mainItem)
              const isOpen = openCategorySlug === mainCatObj?.slug
              if (!isOpen) return null
              return (
                <MainNavMobileSubcategories
                  key={`${mainCatObj?.slug}-subcategory`}
                  item={item}
                  isOpen={isOpen}
                  handleCloseMenu={handleCloseMenu}
                  registerRef={registerSubcategoryRef}
                />
              )
            })}
        </nav>

        <MainNavMobileBottom
          user={user}
          profileLink={profileLink}
          logOut={logOut}
          handleCloseMenu={handleCloseMenu}
        />
      </div>
    </div>
  )
}

function MainNavMobileList({
  menuItems,
  hasShopMenu,
  onCategoryClick,
  onLinkClick,
}: {
  menuItems: MenuItemsType
  hasShopMenu: boolean
  onCategoryClick: (slug: string) => void
  onLinkClick: () => void
}) {
  const { isEnabled } = useFeatureFlag()

  // Shop is a static entry (not data-driven). Per design it sits right after
  // Venues, falling back to the end of the list if Venues is ever absent.
  const venuesIndex = menuItems.findIndex(
    (item) => item.mainItem.metadata?.name === 'Venues'
  )
  // Affiliate Shop is hidden until the `affiliateShop` feature flag is enabled.
  const shopLabel = (
    <span className={s.navLinkLabel}>
      Shop
      <NewBadge className={s.navNewBadge} />
    </span>
  )
  const shopNavItem = !isEnabled('affiliateShop') ? null : hasShopMenu ? (
    // With curated content Shop expands like a category, arrow and all, rather
    // than navigating straight to the shop.
    <li className={s.navItem} key="shop">
      <button
        className={cx(s.navLink, s.navLinkSuppliers)}
        onClick={() => onCategoryClick(SHOP_SLUG)}
      >
        {shopLabel} <ArrowRight />
      </button>
    </li>
  ) : (
    <li className={s.navItem} onClick={onLinkClick} key="shop">
      <Link
        href={ROUTES.SHOP.ROOT}
        onClick={() => navItemOnClickGTMEvent('Shop')}
        className={cx(s.navLink, s.navLinkSuppliers)}
      >
        {shopLabel}
      </Link>
    </li>
  )

  return (
    <ul className={s.navList}>
      {menuItems.map((item, index) => {
        const mainCatObj = getMainCategoryObject(item.mainItem)
        const isSuppliers = ['Suppliers', 'Venues'].includes(
          item.mainItem.metadata?.name || ''
        )

        return (
          <Fragment key={mainCatObj?.slug}>
            {item.subcategories.length ? (
              <li className={s.navItem}>
                <button
                  className={cx(s.navLink, {
                    [s.navLinkSuppliers]: isSuppliers,
                  })}
                  onClick={() => {
                    onCategoryClick(mainCatObj?.slug || '')
                  }}
                >
                  {mainCatObj?.name} <ArrowRight />
                </button>
              </li>
            ) : (
              <li className={s.navItem} onClick={onLinkClick}>
                <Link
                  href={`/${mainCatObj?.slug}`}
                  onClick={() => navItemOnClickGTMEvent(mainCatObj?.name)}
                  className={cx(s.navLink, {
                    [s.navLinkSuppliers]: isSuppliers,
                  })}
                >
                  {mainCatObj?.name}
                </Link>
              </li>
            )}
            {index === venuesIndex && shopNavItem}
          </Fragment>
        )
      })}
      {venuesIndex === -1 && shopNavItem}
    </ul>
  )
}

function MainNavMobileSubcategories({
  item,
  isOpen,
  handleCloseMenu,
  registerRef,
}: {
  item: MenuItemsType[0]
  isOpen: boolean
  handleCloseMenu: () => void
  registerRef: (slug: string, ref: HTMLDivElement) => void
}) {
  const mainCatObj = getMainCategoryObject(item.mainItem)
  const subcategories = splitSubcategories(item.subcategories)

  return (
    <div
      className={cx(s.navList, s.navListSubcategory, { [s.open]: isOpen })}
      ref={(el) => {
        if (el) registerRef(mainCatObj?.slug || '', el)
      }}
    >
      <div className={s.subcategoryTitle} onClick={handleCloseMenu}>
        <Link
          href={`/${mainCatObj?.slug || ''}`}
          className={s.subcategoryTitleLink}
          onClick={() => navItemOnClickGTMEvent(mainCatObj?.name)}
        >
          All {mainCatObj?.name}
          <ArrowLeftLong
            svgProps={{
              style: { transform: 'rotate(180deg)' },
              width: 16,
              height: 16,
            }}
          />
        </Link>
      </div>
      {subcategories.map((row, idx) => (
        <NavSubcategoryList
          key={idx}
          row={row}
          mainCatObj={mainCatObj}
          handleCloseMenu={handleCloseMenu}
          variant="mobile"
        />
      ))}
    </div>
  )
}

function MainNavMobileBottom({
  user,
  profileLink,
  logOut,
  handleCloseMenu,
}: {
  user: any
  profileLink: string
  logOut: () => void
  handleCloseMenu: () => void
}) {
  return (
    <div className={s.bottomWrapper}>
      {user ? (
        <div className={s.bottomLinkWrapper} onClick={handleCloseMenu}>
          <NavButton href={profileLink}>
            <UserIcon /> Your Dashboard
          </NavButton>
          <NavButton elementProps={{ onClick: logOut }} type="secondary">
            Log Out
          </NavButton>
        </div>
      ) : (
        <div className={s.bottomLinkWrapper} onClick={handleCloseMenu}>
          <NavButton href={ROUTES.AUTH.SIGN_IN} type="secondary">
            <UserIcon iconColor="#fff" /> Log in
          </NavButton>
          <NavButton href={ROUTES.AUTH.SIGN_UP}>Create Account</NavButton>
        </div>
      )}
    </div>
  )
}
